import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTranslation } from 'react-i18next';
import { useWithdrawalAnalysis } from '../hooks/useWithdrawalAnalysis.js';
import type {
  CancellationReasonItem,
  WithdrawalAnalysisResponse,
  FreeTextAnswer,
  MonthlyCancellationTrend,
} from '../types/withdrawal-analysis.js';

/**
 * @graph-connects none
 */
function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * @graph-connects none
 */
function formatDate(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * @graph-connects none
 */
function MetricCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span
        className="text-xs font-medium uppercase tracking-wider"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {label}
      </span>
      <span className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
        {value}
      </span>
      {sub ? (
        <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          {sub}
        </span>
      ) : null}
    </Card>
  );
}

/**
 * @graph-connects none
 */
function ReasonList({
  title,
  items,
  barColor,
}: {
  title: string;
  items: CancellationReasonItem[];
  barColor: string;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <Card className="p-4">
      <h3
        className="mb-3 text-sm font-semibold uppercase tracking-wider"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {title}
      </h3>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.optionText} className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className="truncate text-sm"
                  style={{ color: 'var(--color-text-primary)' }}
                  title={item.optionText}
                >
                  {item.optionText}
                </span>
                <span
                  className="shrink-0 text-xs tabular-nums"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {t('withdrawal.countUnit', { num: String(item.count) })} (
                  {formatPct(item.percentage)})
                </span>
              </div>
              <div
                className="mt-1 h-1.5 rounded-full"
                style={{ backgroundColor: 'var(--color-border)' }}
              >
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${String(Math.max(item.percentage * 100, 2))}%`,
                    backgroundColor: barColor,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * @graph-connects none
 */
function TrendTable({ rows }: { rows: MonthlyCancellationTrend[] }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <Card className="overflow-x-auto p-4">
      <h3
        className="mb-3 text-sm font-semibold uppercase tracking-wider"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {t('withdrawal.monthlyTrend')}
      </h3>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
            <th
              className="px-2 py-1 text-left font-medium"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {t('withdrawal.month')}
            </th>
            <th
              className="px-2 py-1 text-right font-medium"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {t('withdrawal.activeMembers')}
            </th>
            <th
              className="px-2 py-1 text-right font-medium"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {t('withdrawal.cancelledCount')}
            </th>
            <th
              className="px-2 py-1 text-right font-medium"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {t('withdrawal.cancellationRate')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.month} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td
                className="px-2 py-1.5 tabular-nums"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {row.month}
              </td>
              <td
                className="px-2 py-1.5 text-right tabular-nums"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {row.activeCount.toLocaleString()}
              </td>
              <td
                className="px-2 py-1.5 text-right tabular-nums"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {row.cancelledCount}
              </td>
              <td
                className="px-2 py-1.5 text-right tabular-nums"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {formatPct(row.cancellationRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/**
 * @graph-connects none
 */
function FreeTextList({ answers }: { answers: FreeTextAnswer[] }) {
  const { t } = useTranslation();
  if (answers.length === 0) return null;
  return (
    <Card className="p-4">
      <h3
        className="mb-3 text-sm font-semibold uppercase tracking-wider"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {t('withdrawal.freeTextAnswers')}
      </h3>
      <div className="space-y-3">
        {answers.map((a, i) => (
          <div
            key={`${a.answeredAt}-${String(i)}`}
            className="rounded-md p-3"
            style={{ backgroundColor: 'var(--color-bg-base)' }}
          >
            <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
              {a.text}
            </p>
            <span className="mt-1 block text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {formatDate(a.answeredAt)}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * @graph-connects none
 */
function Dashboard({ data }: { data: WithdrawalAnalysisResponse }) {
  const { t } = useTranslation();
  const { summary } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <MetricCard
          label={t('withdrawal.activeMembers')}
          value={summary.activeMembers.toLocaleString()}
        />
        <MetricCard label={t('withdrawal.cancelRequested')} value={summary.cancelRequested} />
        <MetricCard label={t('withdrawal.canceled')} value={summary.canceled} />
        <MetricCard
          label={t('withdrawal.cancellationRate')}
          value={formatPct(summary.cancellationRate)}
          sub={`${String(summary.cancelRequested + summary.canceled)} / ${String(summary.totalMembers)}`}
        />
        <MetricCard
          label={t('withdrawal.supportDissatisfactionRate')}
          value={formatPct(summary.supportDissatisfactionRate)}
          sub={t('withdrawal.supportDissatisfactionNote')}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ReasonList
          title={t('withdrawal.reasonRanking')}
          items={data.cancellationReasons}
          barColor="var(--color-error)"
        />
        <ReasonList
          title={t('withdrawal.satisfactionRanking')}
          items={data.satisfactionPoints}
          barColor="var(--color-accent-primary)"
        />
      </div>

      <TrendTable rows={data.monthlyTrend} />

      <FreeTextList answers={data.freeTextAnswers} />
    </div>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 退会者分析ダッシュボード。アクティブ会員数・解約率・解約理由ランキング・満足点・月次トレンド・フリーテキスト回答を可視化する。
 * @graph-connects useWithdrawalAnalysis [calls] Withdrawal Analysis data hook
 */
export function WithdrawalAnalysisPage() {
  const { t } = useTranslation();
  const query = useWithdrawalAnalysis();

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
      <div className="mb-6">
        <span
          className="mb-1 block font-mono text-xs uppercase tracking-widest"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {t('withdrawal.title')}
        </span>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
          {t('withdrawal.dashboardTitle')}
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {t('withdrawal.description')}
        </p>
      </div>

      {query.isLoading ? (
        <Card className="flex items-center justify-center p-12">
          <span style={{ color: 'var(--color-text-muted)' }}>{t('app.loading')}</span>
        </Card>
      ) : query.isError ? (
        <Card className="p-6">
          <p style={{ color: 'var(--color-error)' }}>
            {t('common.fetchError')}。{t('common.retryLater')}。
          </p>
        </Card>
      ) : !query.data ? (
        <Card className="p-6">
          <p style={{ color: 'var(--color-text-muted)' }}>{t('withdrawal.noData')}</p>
        </Card>
      ) : (
        <>
          <div className="mb-4 text-right">
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {t('common.lastUpdated')}: {formatDate(query.data.fetchedAt)}
            </span>
          </div>
          <Dashboard data={query.data} />
        </>
      )}
    </div>
  );
}
